import { Link } from 'react-router-dom'
import { ArrowLeft, UserCheck } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import {
  focusAreas,
  loadSelfAssessmentCatalog,
  mean,
  teamPicture,
  type AreaAggregate,
  type PersonStanding,
  type SelfAssessmentCatalog,
} from '@/lib/self-assessment'
import type { SelfAssessment, SelfAssessmentScore, TeamMember } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { RadarChart } from '@/components/radar-chart'
import { ScaleLegend, stepClass } from '@/components/scale-legend'
import { round1 } from '@/components/self-assessment-results'

/**
 * /self-assessment/team — the coach's view of the room. DPL COACH ONLY.
 *
 * The route guard in App.tsx is the courtesy; RLS is the control (0067). A lead
 * who reached this URL would get every member back as "Not started", because
 * the policy returns them no rows at all — the page cannot leak what the
 * database never sends.
 *
 * The aggregate (teamPicture in lib/self-assessment.ts) uses each person's
 * LATEST FINISHED run, so retaking counts once, and an in-progress run (a
 * half-answered area reads lower than it is) counts not at all.
 */
export function SelfAssessmentTeamPage() {
  const { currentTeam } = useAuth()
  const teamId = currentTeam?.id

  const { data, loading, error } = useQuery(async () => {
    if (!teamId) return null
    const catalog = await loadSelfAssessmentCatalog()
    if (!catalog) return null
    const [members, runs, scores] = await Promise.all([
      sb().from('team_members').select('*').eq('team_id', teamId).eq('active', true).order('email'),
      sb()
        .from('self_assessments')
        .select('*')
        .eq('team_id', teamId)
        .eq('template_id', catalog.template.id)
        .order('started_at', { ascending: false }),
      sb().from('self_assessment_scores').select('*').eq('team_id', teamId),
    ])
    for (const r of [members, runs, scores]) if (r.error) throw r.error
    const picture = teamPicture(
      catalog,
      (members.data ?? []) as TeamMember[],
      (runs.data ?? []) as SelfAssessment[],
      (scores.data ?? []) as SelfAssessmentScore[],
    )
    return { catalog, ...picture }
  }, [teamId])

  if (!currentTeam) return null

  let body
  if (loading && !data) body = <Loading />
  else if (!data) {
    body = <EmptyState icon={UserCheck} title="Not available" description="The self-assessment has not been set up yet." />
  } else {
    const finished = data.people.filter((p) => p.latestResults)
    body = (
      <div className="space-y-8">
        <StatusList people={data.people} total={data.catalog.criteria.length} />
        {finished.length === 0 ? (
          <p className="text-sm text-muted-foreground">The team picture appears once someone finishes.</p>
        ) : (
          <>
            <TeamAggregate catalog={data.catalog} aggregate={data.aggregate} people={finished.length} />
            <AreaGrid catalog={data.catalog} finished={finished} />
          </>
        )}
      </div>
    )
  }

  return (
    <Page
      title="Self-assessment — team view"
      description={`Everyone on ${currentTeam.name}. Visible to DPL coaches only — not to the team lead.`}
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link to="/self-assessment">
            <ArrowLeft /> Mine
          </Link>
        </Button>
      }
    >
      {error && <ErrorNote error={error} />}
      {body}
    </Page>
  )
}

/** Where each person is: not started, part-way, or finished (and when). */
function StatusList({ people, total }: { people: PersonStanding<TeamMember>[]; total: number }) {
  const finished = people.filter((p) => p.latest).length
  const inProgress = people.filter((p) => p.open && !p.latest).length
  return (
    <section className="space-y-2">
      <h2 className="flex items-baseline gap-2 text-base font-semibold">
        Status
        <span className="text-xs font-normal text-muted-foreground">
          {finished} finished · {inProgress} in progress · {people.length - finished - inProgress} not started
        </span>
      </h2>
      <Card>
        <CardContent className="divide-y p-0">
          {people.map((p) => {
            const overall = p.latestResults
              ? mean(p.latestResults.map((r) => r.average).filter((v): v is number => v != null))
              : null
            const run = p.latest ?? p.open
            return (
              <div key={p.member.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{p.member.display_name || p.member.email}</div>
                  <div className="truncate text-xs text-muted-foreground">{statusLine(p, total)}</div>
                </div>
                {overall != null && (
                  <span className="text-xs tabular-nums text-muted-foreground">avg {round1(overall)}</span>
                )}
                <Badge variant={p.latest ? 'default' : 'muted'} className="w-28 justify-center">
                  {p.latest ? 'Finished' : p.open ? 'In progress' : 'Not started'}
                </Badge>
                <div className="w-20 shrink-0 text-right">
                  {run && (
                    <Button size="sm" variant="ghost" asChild>
                      <Link to={`/self-assessment/${run.id}`}>Open</Link>
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </CardContent>
      </Card>
    </section>
  )
}

function statusLine(p: PersonStanding<TeamMember>, total: number): string {
  if (!p.member.user_id) return 'Has not signed in yet'
  if (p.latest) {
    const retake = p.open ? ` · retaking, ${p.openAnswered} of ${total}` : ''
    return `Finished ${formatDate(p.latest.completed_at)}${retake}`
  }
  if (p.open) return `In progress · ${p.openAnswered} of ${total} answered`
  return 'Not started'
}

/** The team radar, and every area's average and range with the lowest three marked. */
function TeamAggregate({
  catalog,
  aggregate,
  people,
}: {
  catalog: SelfAssessmentCatalog
  aggregate: AreaAggregate[]
  people: number
}) {
  const focus = new Set(focusAreas(aggregate).map((a) => a.category.id))
  return (
    <section className="space-y-3">
      <h2 className="flex items-baseline gap-2 text-base font-semibold">
        The team picture
        <span className="text-xs font-normal text-muted-foreground">
          latest finished run from {people} {people === 1 ? 'person' : 'people'}
        </span>
      </h2>
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardContent className="pt-5">
            <RadarChart
              axes={aggregate.map((a) => ({ label: a.category.name, now: a.average }))}
              template={catalog.template}
              nowLabel="Team average"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Every area, lowest three marked</CardTitle>
          </CardHeader>
          <CardContent className="divide-y p-0">
            {aggregate.map((a) => (
              <div key={a.category.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {a.category.name}
                  {focus.has(a.category.id) && <span className="ml-2 text-xs font-medium text-warning">focus</span>}
                </span>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {a.min != null && a.max != null ? `${round1(a.min)}–${round1(a.max)}` : ''}
                </span>
                <span className="w-10 text-right font-semibold tabular-nums">
                  {a.average != null ? round1(a.average) : '–'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </section>
  )
}

/** Person × area, each cell on the scale's colour ramp. */
function AreaGrid({
  catalog,
  finished,
}: {
  catalog: SelfAssessmentCatalog
  finished: PersonStanding<TeamMember>[]
}) {
  const { template } = catalog
  const count = template.scale_max - template.scale_min + 1
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">Everyone, by area</h2>
      <ScaleLegend template={template} />
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="px-3 py-2 text-left font-medium">Person</th>
                {catalog.categories.map((c) => (
                  <th key={c.id} className="px-1 py-2 text-center font-medium" title={c.name}>
                    {initials(c.name)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {finished.map((p) => (
                <tr key={p.member.id} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <Link className="hover:underline" to={`/self-assessment/${p.latest!.id}`}>
                      {p.member.display_name || p.member.email}
                    </Link>
                  </td>
                  {p.latestResults!.map((r) => (
                    <td key={r.category.id} className="px-1 py-1.5 text-center">
                      {r.average != null ? (
                        <span
                          className={`${stepClass(Math.round(r.average) - template.scale_min, count)} inline-block min-w-[2.25rem] rounded px-1 py-0.5 tabular-nums`}
                          style={{ background: 'hsl(var(--scale-bg))', color: 'hsl(var(--scale-ink))' }}
                          title={`${r.category.name}: ${round1(r.average)}`}
                        >
                          {round1(r.average)}
                        </span>
                      ) : (
                        '–'
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">
        Columns are the ten SWEBOK areas, abbreviated; hover one for its name.
      </p>
    </section>
  )
}

/** "Software Design, Models and Methods" → "SDMM", for a narrow column. */
function initials(name: string): string {
  return name
    .split(/[\s,]+/)
    .filter((w) => w && /[A-Z]/.test(w[0]))
    .map((w) => w[0])
    .join('')
}
